/**
 * Página Pricing. Precios, límites y módulos vienen del catálogo de planes del SuperAdmin
 * (GET /api/public/planes); acá solo se ponen los textos de venta de cada plan.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Marco, { Navegacion, useMeta } from './Marco';
import { useT } from './idioma';
import { usePlanes } from './planes';

const fmt = (n) => { const v = Math.round(n * 100) / 100; return v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }); };
const FILAS = {
  facturacion: ['facturacion', 'recepcion', 'cxc_cxp'],
  contabilidad: ['contabilidad', 'conciliacion', 'ia', 'reportes_tributarios', 'ordenes_compra', 'dimensiones', 'activos', 'presupuesto'],
  plataforma: ['api', 'suite'],
};

export default function Precios() {
  const T = useT();
  const t = T.precios;
  useMeta(T.meta.precios);
  const { planes, error } = usePlanes();
  const [anual, setAnual] = useState(false);
  // El descuento anual sale del catálogo (precio anual frente a 12 meses), no de un texto fijo
  const descuento = planes?.length ? Math.round((1 - planes[0].precioAnual / (12 * planes[0].precioMensual)) * 100) : 0;
  const nombre = (codigo) => planes?.find(p => p.codigo === codigo)?.nombre || codigo;

  return (
    <Marco>
      <div style={{ borderBottom: '1px solid #E2E5EF' }}><Navegacion tono="clara" /></div>

      <header className="caja" style={{ paddingTop: 88, textAlign: 'center' }}>
        <div className="ojo">{t.ojo}</div>
        <h1 className="display" style={{ fontWeight: 800, fontSize: 'clamp(40px,5.4vw,72px)', margin: '18px auto 0', maxWidth: 940, color: '#1A1D4E' }}>{t.titulo}</h1>
        <p className="suave" style={{ fontSize: 19, margin: '22px auto 0', maxWidth: 700 }}>{t.bajada}</p>
        <div className="alternar" role="group" aria-label={t.periodo}>
          <button type="button" aria-pressed={!anual} onClick={() => setAnual(false)}>{t.mensual}</button>
          <button type="button" aria-pressed={anual} onClick={() => setAnual(true)}>{t.anual(descuento)}</button>
        </div>
      </header>

      <section aria-label={t.ojo} className="caja" style={{ paddingTop: 56 }}>
        {!planes && !error && <p className="suave" style={{ textAlign: 'center' }}>{t.cargando}</p>}
        {error && <p style={{ textAlign: 'center', color: '#922E23' }}>{t.error}</p>}
        {planes && (
          <div className="planes">
            {planes.map(p => {
              const v = t.planes[p.codigo] || { para: p.descripcion, puntos: [] };
              const precio = anual ? p.precioAnual / 12 : p.precioMensual;
              const clase = `plan${p.destacado ? ' destacado' : ''}${v.porCliente ? ' despacho' : ''}`;
              return (
                <article key={p.codigo} className={clase}>
                  {p.destacado && <span className="marca">{t.masElegido}</span>}
                  <h2>{p.nombre}</h2>
                  <p className="para">{v.para}</p>
                  <div style={{ marginTop: 18, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span className="monto">${fmt(Math.round(precio))}</span><span className="suave">{t.mes}</span>
                  </div>
                  <div className="suave" style={{ fontSize: 13, marginTop: 8 }}>{anual ? t.facturaAnual(fmt(p.precioAnual)) : t.facturaMensual}</div>
                  <div className="etiquetas"><span>{t.empresas(p.maxEmpresas)}</span><span>{t.usuarios(p.maxUsuarios)}</span></div>
                  <Link to="/demo" className={`accion pildora ${p.destacado ? 'primaria' : 'borde-claro'}`} style={{ borderRadius: 10 }}>{v.porCliente ? t.hablemos : T.nav.demo}</Link>
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 24 }}>{v.porCliente ? t.porCliente : v.base ? t.todoMas(nombre(v.base)) : t.incluye}</div>
                  <ul>{v.puntos.map(x => <li key={x}>{x}</li>)}</ul>
                  <div className="suave" style={{ fontSize: 13, marginTop: 'auto', paddingTop: 22 }}>
                    {[p.empresaAdicional != null && t.adicional(fmt(p.empresaAdicional)), v.extra].filter(Boolean).join(' · ')}
                  </div>
                </article>
              );
            })}
          </div>
        )}
        <p className="suave" style={{ fontSize: 13.5, margin: '22px 0 0', textAlign: 'center' }}>{t.nota}</p>
      </section>

      {planes && (
        <section aria-label={t.comparar} className="caja" style={{ paddingTop: 110 }}>
          <h2 className="display" style={{ fontWeight: 800, fontSize: 'clamp(30px,3.4vw,44px)', color: '#1A1D4E' }}>{t.comparar}</h2>
          <div className="tabla">
            <table>
              <thead><tr><th scope="col" style={{ textAlign: 'left', paddingLeft: 22, width: '36%' }}>{t.caracteristica}</th>{planes.map(p => <th key={p.codigo} scope="col" style={p.destacado ? { color: '#4652B8' } : undefined}>{p.nombre}</th>)}</tr></thead>
              <tbody>
                <tr className="seccion"><th scope="colgroup" colSpan={planes.length + 1}>{t.secciones.capacidad}</th></tr>
                <tr><th scope="row">{t.filas.precio}</th>{planes.map(p => <td key={p.codigo} className={p.destacado ? 'resalta' : ''}>${fmt(p.precioMensual)}</td>)}</tr>
                <tr><th scope="row">{t.filas.empresas}</th>{planes.map(p => <td key={p.codigo} className={p.destacado ? 'resalta' : ''}>{p.maxEmpresas}</td>)}</tr>
                <tr><th scope="row">{t.filas.usuarios}</th>{planes.map(p => <td key={p.codigo} className={p.destacado ? 'resalta' : ''}>{p.maxUsuarios}</td>)}</tr>
                <tr><th scope="row">{t.filas.adicional}</th>{planes.map(p => <td key={p.codigo} className={p.destacado ? 'resalta' : ''}>{p.empresaAdicional != null ? `$${fmt(p.empresaAdicional)}` : '—'}</td>)}</tr>
                <tr><th scope="row">{t.filas.documentos}</th>{planes.map(p => <td key={p.codigo} className={p.destacado ? 'resalta' : ''}>{t.filas.ilimitados}</td>)}</tr>
                {Object.entries(FILAS).map(([sec, mods]) => [
                  <tr key={`s-${sec}`} className="seccion"><th scope="colgroup" colSpan={planes.length + 1}>{t.secciones[sec]}</th></tr>,
                  ...mods.map(m => (
                    <tr key={`m-${m}`}><th scope="row">{t.filas[m]}</th>
                      {planes.map(p => { const si = p.modulos.includes(m); return <td key={p.codigo} className={`${si ? '' : 'no'} ${p.destacado ? 'resalta' : ''}`}>{si ? '✓' : '—'}</td>; })}
                    </tr>
                  )),
                ])}
                <tr><th scope="row">{t.filas.soporte}</th>{planes.map(p => <td key={p.codigo} className={p.destacado ? 'resalta' : ''}>{t.soporte[p.codigo] || '—'}</td>)}</tr>
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section aria-label={t.preguntasTitulo} className="caja" style={{ paddingTop: 110, paddingBottom: 110 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 48 }}>
          <div style={{ flex: '1 1 300px', maxWidth: 380 }}>
            <h2 className="display" style={{ fontWeight: 800, fontSize: 'clamp(30px,3.4vw,44px)', lineHeight: 1.08, color: '#1A1D4E' }}>{t.preguntasTitulo}</h2>
            <p className="suave" style={{ fontSize: 17, margin: '18px 0 0' }}>{t.algoMas} <Link to="/demo" style={{ fontWeight: 600 }}>{t.preguntenos} ›</Link></p>
          </div>
          <div style={{ flex: '999 1 520px', minWidth: 0 }}>
            {t.preguntas.map(([q, a], n) => (
              <div key={q} style={{ borderTop: '1px solid #E2E5EF', borderBottom: n === t.preguntas.length - 1 ? '1px solid #E2E5EF' : 0, padding: '24px 0' }}>
                <h3 className="display" style={{ fontWeight: 700, fontSize: 19, letterSpacing: '-.01em' }}>{q}</h3>
                <p className="suave" style={{ margin: '10px 0 0' }}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="caja" style={{ paddingBottom: 110 }}>
        <div className="noche" style={{ borderRadius: 24, padding: '64px 48px', display: 'flex', flexWrap: 'wrap', gap: 32, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ maxWidth: 640 }}>
            <h2 className="display" style={{ fontWeight: 800, fontSize: 'clamp(28px,3.2vw,42px)' }}>{t.dudaTitulo}</h2>
            <p className="suave" style={{ fontSize: 18, margin: '14px 0 0' }}>{t.dudaTexto}</p>
          </div>
          <Link to="/demo" className="pildora primaria" style={{ minHeight: 52 }}>{T.nav.demo}</Link>
        </div>
      </section>
    </Marco>
  );
}
